import React from "react";
import { EmptyState } from "@ad/anq-ui";
import { ColumnsType } from "@ad/anq-ui/lib/_components/_table/interface";
import { QuestionNotice } from "components/questionNotice";
import type { RankDataItem } from "@/api/search_analysis/interface";
import dynamic from "next/dynamic";
import { KeywordRankType } from "./config";
import type { QueryKeywordRankParam } from "@/api/search_analysis/interface";
import { queryKeywordRank } from "@/api/search_analysis";
import { useEffect, useState } from "react";
import DmpTable from "components/DmpTable";
import { RANK_IMAGES } from "@/common/constant";

const WordCloud = dynamic(() => import("./wordCloud"), {
  ssr: false,
});

export type ColumnsDataType = Pick<
  RankDataItem,
  "rankNum" | "word" | "incrIdx"
>;
export type Cloud = { text: string; value: number };

interface Props {
  cloud?: Cloud[];
  rankList?: ColumnsDataType[];
  catId?: number;
  onWord?: (v: string) => void;
}

const formatIndex = (index: number) => (
  <div style={{ textAlign: "center" }}>
    {[1, 2, 3].includes(index) ? (
      <img width={26} src={RANK_IMAGES[index]} />
    ) : (
      index
    )}
  </div>
);

const WordList = ({ catId, onWord = () => {} }: Props) => {
  const [rankList, setRank] = useState<Array<ColumnsDataType>>([]);
  const [cloud, setCloud] = useState<Array<Cloud>>([]);

  const columns: ColumnsType<ColumnsDataType> = [
    {
      title: <div style={{ textAlign: "center" }}>排名</div>,
      dataIndex: "rankNum",
      width: 80,
      render: (t, r, i) => formatIndex(i + 1),
    },
    {
      title: "热词",
      dataIndex: "word",
      render: (t) => (
        <span
          className="dmp-link cursor-pointer text-sm"
          onClick={() => onWord(t)}
        >
          {t}
        </span>
      ),
    },
    {
      title: "飙升指数",
      dataIndex: "incrIdx",
      width: 200,
    },
  ];

  const fetchData = async (keywordRankType: KeywordRankType) => {
    const param: QueryKeywordRankParam = {
      catId,
      keywordRankType,
    };

    const ret = await queryKeywordRank(param);

    if (ret?.success) {
      if (keywordRankType === KeywordRankType.INCR) {
        /** 飙升 */
        setRank(
          ret?.result?.rankData?.map((item) => ({
            rankNum: item.rankNum,
            word: item.word,
            incrIdx: item.incrIdx,
          }))
        );
      }

      if (keywordRankType === KeywordRankType.WELL) {
        /** 热搜 */
        setCloud(
          ret?.result?.rankData?.map((item) => ({
            text: item.word,
            value: item.searchIdx,
          }))
        );
      }
    }
  };

  useEffect(() => {
    if (catId) {
      fetchData(KeywordRankType.WELL);
      fetchData(KeywordRankType.INCR);
    }
  }, [catId]);

  // TODO: 这里的布局曾经是flex, 左边是热搜词右边是飙升词排行;
  // 但因为不明原因, 产品的电脑上, 浏览器主屏移动到副屏, 会导致右边的一半的宽度, 会一直缓慢的放大
  return (
    <div className="overflow-hidden">
      <div className="float-left mr-4 w-[calc(50%-8px)]">
        <div className="mb-4 flex items-center text-base font-semibold">
          <span className="mr-1 text-sm">热搜词</span>
          <QuestionNotice title="商家所选类目下，按照搜索次数等综合排行的榜单，数据信息并非实时更新，有一定滞后性，仅供参考" />
        </div>
        <div className="h-[430px] overflow-hidden rounded-lg border border-[#ebecf2]">
          {cloud.length ? (
            <WordCloud
              data={cloud.map((item) => ({
                name: item.text,
                value: item.value,
              }))}
              onWord={onWord}
            />
          ) : (
            <div className="pt-[160px]">
              <EmptyState
                description={<span className="text-sm">暂无数据</span>}
              />
            </div>
          )}
        </div>
      </div>
      <div className="float-left w-[calc(50%-8px)]">
        <div className="mb-4 flex items-center text-base font-semibold">
          <span className="mr-1 text-sm">飙升词排行</span>
          <QuestionNotice title="商家所选类目下，按照搜索次数变化量等综合排行的榜单，数据信息并非实时更新，有一定滞后性，仅供参考" />
        </div>
        <div className="max-h-[430px] overflow-hidden rounded-lg">
          <DmpTable
            columns={columns}
            dataSource={rankList}
            scroll={{ y: rankList.length ? 382 : undefined }}
            rowKey="word"
            className="flow_analysis-word_list"
          />
        </div>
      </div>
      <style>
        {`
          .flow_analysis-word_list .anq-table-empty .anq-table-empty {
            height: 350px;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        `}
      </style>
    </div>
  );
};

export default WordList;
